<template>
  <!-- 
    This container will only show banners if the user is *not* in setup mode
    (which is now handled by the router) and has active messages.
  -->
  <div class="banner-container" ref="bannerContainerRef">
    <div v-if="allMessages.length > 0" class="banner" :class="bannerLevel">
      <!-- Show a title if there are multiple messages -->
      <strong v-if="allMessages.length > 1"
        >Please address the following items:</strong
      >

      <!-- Single message case -->
      <div v-if="allMessages.length === 1">
        {{ allMessages[0].message }}
        <router-link
          v-if="allMessages[0].link"
          :to="allMessages[0].link"
          class="banner-link"
        >
          Fix now
        </router-link>
      </div>

      <!-- Multiple messages case -->
      <ul v-if="allMessages.length > 1" class="banner-message-list">
        <li v-for="msg in allMessages" :key="msg.message">
          {{ msg.message }}
          <router-link v-if="msg.link" :to="msg.link" class="banner-link">
            (Fix now)
          </router-link>
        </li>
      </ul>
    </div>
  </div>

  <router-view />

  <div v-if="isLoading" class="loading-overlay">
    <div class="spinner"></div>
  </div>
</template>

<script setup>
// Import ref, nextTick, and onMounted from vue
import { computed, watch, ref, nextTick, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()

// Reactively get the loading status from the store
const isLoading = computed(() => store.getters.isLoading)

// Get the full status object from the store
const businessStatus = computed(() => store.getters.businessStatus)
// Get setup mode status from store (to prevent banners from showing)
const isInSetupMode = computed(() => store.getters.isInSetupMode)

// GLOBAL SETUP COMPLETION LOGIC ---

// 4. Add the 6 computed getters from the wizard
const hasLocation = computed(() => store.getters.hasLocation)
const hasService = computed(() => store.getters.hasService)
const hasSchedule = computed(() => store.getters.hasSchedule)
const isAssignedService = computed(() => store.getters.isAssignedService)
const isAssignedEmployee = computed(() => store.getters.isAssignedEmployee)

// 5. Add the aggregate computed property
const allStepsComplete = computed(() => {
  // We only care about this if we're *currently* in setup mode.
  if (isInSetupMode.value === false) {
    return false
  }
  return (
    hasLocation.value &&
    hasService.value &&
    isAssignedService.value &&
    isAssignedEmployee.value &&
    hasSchedule.value
  )
})

// 6. Add the global watch (this replaces the one in the wizard)
watch(
  [allStepsComplete, isInSetupMode],
  async ([isComplete, isSetup]) => {
    // We only proceed if all steps are done AND we are still in setup mode
    if (isComplete && isSetup) {
      await store.dispatch('completeSetup')

      // Now that setup is done, send the user to the dashboard
      router.push({ name: 'Dashboard' })
    }
  },
  { immediate: true }, // Run immediately on app load
)

// --- START: Consolidated Banner Logic (for post-setup) ---

// Messages are now objects with a `message` and optional `link`
const criticalMessages = computed(() => {
  const messages = []
  // Don't show banners if status isn't loaded or we are in setup mode
  if (!businessStatus.value || isInSetupMode.value) return messages

  // Critical errors (e.g., plan limits)
  if (!businessStatus.value.is_subscription_active) {
    messages.push({
      message:
        'Your subscription is not active. Please update your billing information. No appointments can be created/modified until this is resolved.',
      link: { name: 'Billing' },
    })
  }
  if (
    businessStatus.value.location_limit !== null &&
    businessStatus.value.location_count > businessStatus.value.location_limit
  ) {
    messages.push({
      message: `You are over your plan's location limit (${businessStatus.value.location_count} / ${businessStatus.value.location_limit}). No appointments can be created/modified until this is resolved. Delete locations or upgrade your plan.`,
      link: { name: 'Billing' },
    })
  }
  if (
    businessStatus.value.employee_limit !== null &&
    businessStatus.value.employee_count > businessStatus.value.employee_limit
  ) {
    messages.push({
      message: `You are over your plan's employee limit (${businessStatus.value.employee_count} / ${businessStatus.value.employee_limit}). No appointments can be created/modified until this is resolved. Delete employees or upgrade your plan.`,
      link: { name: 'Billing' },
    })
  }
  if (
    businessStatus.value.appointment_limit !== null &&
    businessStatus.value.appointment_limit !== 0 && // 0 means "unlimited"
    businessStatus.value.last_30d_appointment_count >
      businessStatus.value.appointment_limit
  ) {
    messages.push({
      message: `You are over your plan's 30 day appointment limit (${businessStatus.value.last_30d_appointment_count} / ${businessStatus.value.appointment_limit}). No appointments can be created/modified until this is resolved. Please try again later or upgrade your plan.`,
      link: { name: 'Billing' },
    })
  }
  return messages
})

const warningMessages = computed(() => {
  const messages = []
  // Don't show banners if status isn't loaded or we are in setup mode
  if (!businessStatus.value || isInSetupMode.value) return messages

  // Warnings for *new* unassigned items after setup
  if (businessStatus.value.has_unassigned_services) {
    messages.push({
      message:
        'You have one or more services that are not assigned to any location. Appointments cannot be booked for these services until they are assigned.',
      link: { name: 'Services' },
    })
  }
  if (businessStatus.value.has_unassigned_employees) {
    messages.push({
      message:
        'You have one or more employees that are not assigned to any location. Appointments cannot be booked for these employees until they are assigned.',
      link: { name: 'Employees' },
    })
  }
  if (businessStatus.value.has_unassigned_schedule) {
    messages.push({
      message:
        'You have one or more employees that are not assigned any hours or schedule. Appointments cannot be booked for these employees until a schedule is assigned.',
      link: { name: 'ScheduleSettings' },
    })
  }

  // These banners will now only show if a user deletes their last item
  if (businessStatus.value.location_count === 0) {
    messages.push({
      message:
        'You have no locations. Please add one. Appointments cannot be booked without a location.',
      link: { name: 'Locations' },
    })
  }
  if (businessStatus.value.service_count === 0) {
    messages.push({
      message:
        'You have no services. Please add one. Appointments cannot be booked without a service.',
      link: { name: 'Services' },
    })
  }
  return messages
})

// Combine, with critical messages first
const allMessages = computed(() => {
  return [...criticalMessages.value, ...warningMessages.value]
})

// Determine the single banner's class
const bannerLevel = computed(() => {
  if (criticalMessages.value.length > 0) return 'banner-critical'
  if (warningMessages.value.length > 0) return 'banner-warning'
  return ''
})

// Dynamic Layout Offset Logic

// 1. Create a ref for the banner container element
const bannerContainerRef = ref(null)

// 2. Function to update the CSS variable
const updateLayoutOffset = () => {
  let bannerHeight = 0
  // Check if the banner element exists and has a height
  if (bannerContainerRef.value && bannerContainerRef.value.clientHeight > 0) {
    bannerHeight = bannerContainerRef.value.clientHeight
  }

  // 3. Set the CSS variable on the root element
  document.documentElement.style.setProperty(
    '--layout-top-offset',
    `${bannerHeight}px`,
  )
}

// 4. Watch the number of messages
watch(
  () => allMessages.value.length,
  async () => {
    // Wait for the DOM to re-render *after* the message list changes
    await nextTick()
    updateLayoutOffset()
  },
  { immediate: true }, // Run this immediately on load
)

// 5. Also run it on mount just to be safe
onMounted(() => {
  updateLayoutOffset()
})
</script>

<style lang="scss">
// Import Main styles for this application
@import 'styles/style';
@import '../node_modules/@syncfusion/ej2-base/styles/material.css';
@import '../node_modules/@syncfusion/ej2-buttons/styles/material.css';
@import '../node_modules/@syncfusion/ej2-calendars/styles/material.css';
@import '../node_modules/@syncfusion/ej2-dropdowns/styles/material.css';
@import '../node_modules/@syncfusion/ej2-inputs/styles/material.css';
@import '../node_modules/@syncfusion/ej2-navigations/styles/material.css';
@import '../node_modules/@syncfusion/ej2-popups/styles/material.css';
@import '../node_modules/@syncfusion/ej2-vue-schedule/styles/material.css';

// --- Banner Styles ---

.banner-container {
  position: sticky; // Sticks to the top of the viewport
  top: 0;
  width: 100%;
  // This z-index should be *below* app's main header (e.g., 1020)
  // but above page content.
  z-index: 1101;
}

.banner {
  padding: 12px 20px;
  color: #fff;
  font-weight: 500;
  // Align left for lists
  text-align: left;

  &.banner-critical {
    background-color: #e74c3c; // Red
    border-bottom: 1px solid #c0392b;
  }

  &.banner-warning {
    background-color: #f39c12; // Yellow/Orange
    color: #333;
    border-bottom: 1px solid #e67e22;
  }

  // Title for multi-message
  strong {
    display: block;
    margin-bottom: 8px;
    font-size: 1.1em;
  }

  // Link style
  .banner-link {
    color: inherit;
    font-weight: bold;
    text-decoration: underline;
    margin-left: 8px;

    .banner-warning & {
      color: #222;
    }
  }
}

// Styles for the message list
.banner-message-list {
  margin: 0;
  padding-left: 20px; /* Indent the list items */

  li {
    margin-bottom: 5px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}

// --- Loading Overlay Styles ---

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; // Ensure it's on top of everything
}

.spinner {
  border: 5px solid #f3f3f3; /* Light grey */
  border-top: 5px solid #3498db; /* Blue */
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// The backdrop must be HIGHER than the sidebar (1100)
.modal-backdrop {
  z-index: 1102;
}

// The modal itself must be HIGHER than its backdrop (1101)
.modal {
  z-index: 1103;
}
</style>
